import { DUPE_API_URL } from '@dupe/common/constants'
import { useQuery } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import useViewerAuthHeaders from '~/src/hooks/useViewerAuthHeaders'

/** A bottle on one of the shopper's lists, as the Library menu draws it. */
export interface LibraryBottle {
  entityId: string
  brand: string
  name: string
  imageUrl: string | null
  href: string
}

type BottleList = 'owned' | 'want'

/** Every Library bottle query starts with this, so one call refreshes them all. */
export const LIBRARY_BOTTLES_KEY = 'library-bottles'

async function fetchBottles(
  list: BottleList,
  headers: Readonly<Record<string, string>>,
  signal?: AbortSignal
): Promise<LibraryBottle[]> {
  const res = await fetch(
    `${DUPE_API_URL}/api/user/collection?list=${list}&entityType=fragrance_item&details=1`,
    { headers, signal }
  )
  // Signed out, the fragrance chat off for them, or a failed read: no section.
  if (!res.ok) return []
  const body = (await res.json()) as {
    data?: {
      items?: {
        entityId: string
        bottle: Omit<LibraryBottle, 'entityId'> | null
      }[]
    }
  }
  // The api lists oldest first; the menu leads with the newest.
  return (body.data?.items ?? [])
    .flatMap((item) =>
      item.bottle ? [{ entityId: item.entityId, ...item.bottle }] : []
    )
    .reverse()
}

/** Their hearted bottles (the My bottles card's "Love"), newest first. */
async function fetchLoved(
  headers: Readonly<Record<string, string>>,
  signal?: AbortSignal
): Promise<LibraryBottle[]> {
  const res = await fetch(
    `${DUPE_API_URL}/api/user/fragrance-reactions?details=1`,
    { headers, signal }
  )
  if (!res.ok) return []
  const body = (await res.json()) as {
    data?: { heartedBottles?: LibraryBottle[] }
  }
  // The api lists oldest first; the menu leads with the newest.
  return (body.data?.heartedBottles ?? []).slice().reverse()
}

/**
 * The bottles the shopper loves, owns and wants, with names and pictures, for the
 * header's Library menu. Works for signed-out visitors too (their temp id).
 * Empty when the fragrance chat is off for them: the api answers no items.
 */
export default function useLibraryBottles(enabled: boolean) {
  const { authUser } = useAuthUser()
  const { tempUserId } = useGetTempUserId()
  const headers = useViewerAuthHeaders({ authUser, tempUserId })
  const viewer = headers['x-user-id'] ?? headers['x-temp-user-id'] ?? null
  // A signed-in viewer needs its token, or the api answers 401.
  const ready = headers['x-user-id']
    ? Boolean(headers['x-authorization'])
    : Boolean(headers['x-temp-user-id'])

  const query = (list: BottleList) => ({
    queryKey: [LIBRARY_BOTTLES_KEY, viewer, list],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchBottles(list, headers, signal),
    enabled: enabled && ready,
    staleTime: 60 * 1000,
  })
  const owned = useQuery(query('owned'))
  const wanted = useQuery(query('want'))
  const loved = useQuery({
    queryKey: [LIBRARY_BOTTLES_KEY, viewer, 'loved'],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchLoved(headers, signal),
    enabled: enabled && ready,
    staleTime: 60 * 1000,
  })

  return {
    loved: loved.data ?? [],
    owned: owned.data ?? [],
    wanted: wanted.data ?? [],
  }
}
